import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

/**
 * Compact one-line breadcrumb used only on xs (mobile) screens, shown above
 * the page title inside TitleBox. Mirrors the same path/labels/targets as
 * the desktop breadcrumb (TitleBreadCrumbs), but renders as a single small
 * grey line that truncates from the START (oldest ancestors first) when the
 * full path doesn't fit, keeping the closest parents/current step visible.
 */
const MobileTitleBreadCrumbs = ({ title }) => {
  const location = useLocation();
  const containerRef = useRef(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  // Bumped on every content change or container resize. hiddenCount alone
  // can't be relied on to trigger a re-measure: if a resize happens while
  // hiddenCount is already 0 (or already at its previous value), calling
  // setHiddenCount with that same value is a no-op in React and would never
  // schedule a re-render, so the "does it fit now" check below would never
  // run again. remeasureTick always changes, so it reliably forces a fresh
  // measurement pass every time.
  const [remeasureTick, setRemeasureTick] = useState(0);

  const pathnames = location.pathname.split('/').filter((x) => x);
  const titlenames = (title || '').split('/').filter((x) => x);

  // Same linking rule as the desktop TitleBreadCrumbs: only the 2nd segment
  // (index 1) is a link, pointing at the current section's root path.
  const segments = titlenames.map((label, idx) => ({
    label,
    to: idx === 1 && pathnames[0] ? `/${pathnames[0]}` : null,
  }));

  // Content changed (navigated to a different page) -> re-measure from scratch.
  useLayoutEffect(() => {
    setHiddenCount(0);
    setRemeasureTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, location.pathname]);

  // Grow hiddenCount (drop oldest/leftmost segment) until the line fits.
  // Depends on remeasureTick so it always re-checks after a content change
  // or resize, even when hiddenCount itself doesn't need to change first.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollWidth > el.clientWidth && hiddenCount < segments.length - 1) {
      setHiddenCount((c) => c + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenCount, remeasureTick, segments.length]);

  // Re-check when the available width changes (resize/orientation change).
  // The state update is deferred to the next animation frame: updating state
  // synchronously inside the ResizeObserver callback can itself trigger a
  // layout change within the same observation cycle, which is what causes
  // the (benign, but noisy) "ResizeObserver loop completed with undelivered
  // notifications" browser warning. Deferring avoids that.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let rafId = null;
    const observer = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setHiddenCount(0);
        setRemeasureTick((t) => t + 1);
      });
    });
    observer.observe(el);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  if (segments.length === 0) return null;

  const visibleSegments = segments.slice(hiddenCount);
  const lastIndex = visibleSegments.length - 1;
  const onlyLastLeft = hiddenCount === segments.length - 1;

  const crumbSx = (idx) => ({
    fontSize: '11px',
    lineHeight: 1.3,
    color: 'text.secondary',
    flexShrink: idx === lastIndex && onlyLastLeft ? 1 : 0,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  });

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        minWidth: 0,
      }}
    >
      {hiddenCount > 0 && (
        <Typography
          component="span"
          sx={{ fontSize: '11px', lineHeight: 1.3, color: 'text.secondary', flexShrink: 0, pr: 0.3 }}
        >
          …
        </Typography>
      )}
      {visibleSegments.map((seg, idx) => (
        <React.Fragment key={`${seg.label}-${hiddenCount}-${idx}`}>
          {idx > 0 && (
            <Typography
              component="span"
              sx={{ fontSize: '11px', lineHeight: 1.3, color: 'text.secondary', px: 0.3, flexShrink: 0 }}
            >
              ›
            </Typography>
          )}
          {seg.to ? (
            <Link component={RouterLink} to={seg.to} underline="hover" sx={crumbSx(idx)}>
              {seg.label}
            </Link>
          ) : (
            <Typography component="span" sx={crumbSx(idx)}>
              {seg.label}
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default MobileTitleBreadCrumbs;
